import { useState, useCallback } from 'react';
import { FieldData } from '@/types/label';

export const useCsvData = () => {
  const [csvData, setCsvData] = useState<FieldData[]>([]);
  const [currentRowIndex, setCurrentRowIndex] = useState(0);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);

  const parseCSV = useCallback((text: string): FieldData[] => {
    let lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    // Skip Excel's sep=, hint if present
    if (lines[0].toLowerCase().startsWith('sep=')) {
      lines = lines.slice(1);
    }

    // Detect delimiter: tab, semicolon, or comma (in order of priority)
    const firstLine = lines[0];
    let delimiter = ',';
    if (firstLine.includes('\t')) {
      delimiter = '\t';
    } else if (firstLine.includes(';')) {
      delimiter = ';';
    }

    // Parse headers
    const headers = firstLine.split(delimiter).map((h) => h.trim().replace(/^["']|["']$/g, ''));
    setCsvHeaders(headers);

    // Parse rows
    const rows: FieldData[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values = delimiter === ',' 
        ? parseCSVLine(line)
        : line.split(delimiter).map(v => v.trim().replace(/^["']|["']$/g, ''));
      
      if (values.length === headers.length && values.some(v => v !== '')) {
        const row: FieldData = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx];
        });
        rows.push(row);
      }
    }

    return rows;
  }, []);

  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"' && !inQuotes) {
        inQuotes = true;
      } else if (char === '"' && inQuotes) {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());

    return values;
  };

  const importCSV = useCallback((file: File): Promise<FieldData[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const data = parseCSV(text);
          setCsvData(data);
          setCurrentRowIndex(0);
          resolve(data);
        } catch (error) {
          reject(new Error('Failed to parse CSV file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }, [parseCSV]);

  const getCurrentRow = useCallback((): FieldData | null => {
    if (csvData.length === 0) return null;
    return csvData[currentRowIndex] || null;
  }, [csvData, currentRowIndex]);

  const nextRow = useCallback(() => {
    setCurrentRowIndex((prev) => Math.min(prev + 1, csvData.length - 1));
  }, [csvData.length]);

  const prevRow = useCallback(() => {
    setCurrentRowIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const goToRow = useCallback((index: number) => {
    setCurrentRowIndex(Math.max(0, Math.min(index, csvData.length - 1)));
  }, [csvData.length]);

  const clearCSV = useCallback(() => {
    setCsvData([]);
    setCsvHeaders([]);
    setCurrentRowIndex(0);
  }, []);

  return {
    csvData,
    csvHeaders,
    currentRowIndex,
    totalRows: csvData.length,
    getCurrentRow,
    nextRow,
    prevRow,
    goToRow,
    importCSV,
    clearCSV,
  };
};
